/**
 * MER-504: case-insensitive division matching at the query boundary.
 *
 * The global division filter carries manifest division ids (lowercase
 * config slugs: "jackson", "central_coast") while warehouse rows store
 * whatever casing the pipeline emitted - Capitalized for DWS
 * ("Jackson"), lowercase for BD ("central_coast"). Probed live
 * 2026-06-11: the casing is INCONSISTENT across tenants, so no single
 * normalization works; a case-sensitive eq/in matches zero rows on
 * DWS and blanks every filtered tile (the MER-477 class at a second
 * boundary).
 *
 * PostgREST `ilike` without wildcards is case-insensitive equality,
 * and an `or=(division.ilike.a,division.ilike.b)` group ANDs with the
 * rest of the query. Values are charset-validated because they are
 * embedded in the or-group syntax: anything outside the slug charset
 * returns null and the caller falls back to the case-sensitive path
 * (never a malformed query).
 *
 * Plain .mjs so the node:test suite imports it directly.
 */

const SLUG_RE = /^[a-z0-9_-]+$/i;

/**
 * Postgres LIKE/ILIKE treats `_` as a single-character wildcard, so a
 * raw "central_coast" pattern would also match "centralXcoast".
 * Backslash-escape the two wildcard characters; the slug charset
 * already excludes `%` and `\` but escape defensively anyway.
 *
 * @param {string} value
 * @returns {string}
 */
export function ilikeEscape(value) {
  return value.replace(/[\\%_]/g, (c) => `\\${c}`);
}

/**
 * Build the or-group body for a multi-value division filter.
 *
 * @param {string} column   resolved column name (usually "division")
 * @param {ReadonlyArray<string>} values
 * @returns {string | null}   "division.ilike.jackson,division.ilike.dallas"
 *   or null when values are empty or any value fails the slug charset.
 */
export function divisionOrClause(column, values) {
  if (!Array.isArray(values) || values.length === 0) return null;
  if (!SLUG_RE.test(column)) return null;
  if (!values.every((v) => typeof v === "string" && SLUG_RE.test(v))) return null;
  return values.map((v) => `${column}.ilike.${ilikeEscape(v)}`).join(",");
}

/** True when a single eq-style division value is safe for ilike. */
export function isIlikeSafe(value) {
  return typeof value === "string" && SLUG_RE.test(value);
}
